import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../Pagination';
import DlBtn from '../../DlBtn';
import { money } from '../../../utils/region';
import {
  payrollReportsApi, reportFileName, PRP_REPORT_KINDS, PrpAdvisory, PrpCheck, PrpColumn, PrpKind, PrpOption, PrpOptions, PrpRefusal, PrpReport, PrpScope,
  PrpScopeOut, PrpScopes,
} from '../../../utils/api/payrollReports';
import type { AwCtx } from '../ctx';
import { card, dmy, errBox, note, num, sectionHead, table, td, th } from '../ui';
import { Dialog, Loader, actBtn, chip, chipOn, emptyLine } from '../dialog';
import { TF, mmYyyy, runStatusLabel, runTypeLabel } from './payrollText';

// ── Payroll › Reports (batch 10 R9 — PAY-20, PAY-21; design §4 R9; the R9 brief §3 package D, §4.4 – §4.10) ──────────────
// The eight payroll reports of ONE RUN (approved, posted or reversed) or of ONE MONTH (its posted runs, R9-3), both taken
// from the shell's period: a scope switch, its picker (runs 100 a page; the months a posted run owns), and eight cards in
// the design's order, each opening a full-screen read-only window — the report's table (100 rows a page), its totals, the
// server's balancing checks, and the Excel and PDF buttons, which spin while they download (DlBtn).
// ⛔ Everything here is a READ (R9-1): no report and no payslip writes a row, so the pane has no write control at all, for
// any seat — and a books login never reaches it (menus.ts staffOnly: no request is sent).
// ⛔ The server decides what is offered (GET /reports/options, always 200, R9-2). A card shows Open only when the report's
// answer is ok, else the sentence saying why (prp_ref_<code>) — never a disabled control that would send a request the
// server is certain to refuse (a refused call pages the admins). A window's Excel / PDF button is drawn only when that
// format is ok. Every figure, total, check and Filing Center note is the server's; this file adds nothing up.
// ⛔ The pane writes only its own URL keys (R9-21): ?rscope=run|month, ?rrun=<id> or ?rym=YYYY-MM, and ?rkind=<kind> for
// the open window (closing drops it) — never the Payroll List's ?run / ?prview / ?remit, never ?menu / ?sub.
// ⛔ (batch 10 low) A link opens its window only on the scope it names. A run the picker's page does not list (another page,
// another period, or a run no report covers) is asked about on its own — GET /reports/options?runId= answers 200 for any
// run of this client, void ones included — and chosen; a month the period does not list, or a run the server does not
// know, falls back to the newest, and the link's ?rkind is dropped (this pane's own replace write) — never a colleague's
// window opened on another run's or month's report.
// ⛔ Statutory names (ภ.ง.ด.1, สปส.) are literals here, handed to a translation as {{form}}.

const PND1 = 'ภ.ง.ด.1';
const SSO_SHORT = 'สปส.';
/** The month-level reports' forms (R9-4): their titles, refusals and Filing Center notes name them. */
const FORM_OF: Partial<Record<PrpKind, string>> = { pnd1: PND1, sso: SSO_SHORT };
/** Which family words each `code` column of each report (§4.9), by report and column key — by report, because one key
 *  means two lists: details' `whtSource` is the engine's (formula / fixed / manual / none), report 5's is D2's
 *  (override / captured / formula). Summary's `item` reads its own row's block: prr_sum_<block>_<item>.
 *  scripts/testPayrollReportsI18n.ts reads every code column of the server's REPORT_COLUMNS and fails on one missing here. */
export const PRP_CODE_FAMILIES: Record<PrpKind, Record<string, string>> = {
  summary: { block: 'prr_sum_', item: 'prr_sum_<block>_' },
  details: { whtSource: 'prr_wht_src_', paymentStatus: 'prr_pay_status_' },
  payslips: { lang: 'prp_lang_', payMethods: 'prr_pay_method_' },
  payments: { recordMethod: 'prr_pay_method_', accountSource: 'prp_acct_src_', paymentStatus: 'prr_pay_status_', methods: 'prr_pay_method_' },
  pnd1: { whtSource: 'prp_fig_src_', source: 'prp_row_src_' },
  sso: { source: 'prp_row_src_' },
  expense: { role: 'prr_entry_role_' },
  journal: { book: 'prp_book_', origin: 'prp_origin_' },
};

/** Details' three year-to-date columns are the figures BEFORE the month (§4.7: calc_json.ytd), where the payslip register's
 *  columns of the same keys include it (R9-11) — so details heads them with words of their own, prp_col_before_<key>. */
export const PRP_COL_BEFORE = ['ytdIncomeSat', 'ytdWithheldSat', 'ytdSsoSat'];
const m2 = (sat: any): string => money((Number(sat) || 0) / 100);
const ymOf = (y: number, m: number): string => `${y}-${String(m).padStart(2, '0')}`;
const okBox: React.CSSProperties = { color: '#166534' };
const badBox: React.CSSProperties = { color: '#B42318' };
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB', color: '#92400E' };
const kindTitle = (t: TF, kind: string): string => t(`prp_kind_${kind}`, { form: FORM_OF[kind as PrpKind] || '' });
/** The scope as a person reads it: the run's number, or the month day-first's way (mm/yyyy). */
const scopeText = (s: PrpScopeOut): string => (s.kind === 'run' ? String(s.docNo || s.tag) : mmYyyy(s.year, s.month));
/** One refusal of an options answer as a sentence. A month with no posted run sends reports 5 and 6 to Tax (R9-4). */
function refusalText(t: TF, kind: string, r: PrpRefusal | undefined, s: PrpScopeOut): string {
  if (!r) return '';
  const month = mmYyyy(Number(r.year || s.year), Number(r.month || s.month));
  const form = FORM_OF[kind as PrpKind];
  if (r.code === 'no_posted_run' && form) return t('prp_ref_no_posted_run_tax', { month, form });
  const k = `prp_ref_${r.code}`;
  return t(k) !== k ? t(k, { status: runStatusLabel(t, String(r.status || s.status || '')), month, form: form || PND1 }) : String(r.code || '');
}
/** A refused report GET (a backstop — the screen asks only for what the options offered) in this screen's words. */
const prpErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  const k = `prp_err_${code}`;
  return code && t(k) !== k ? t(k) : String(e?.message || '');
};

// ── The pane ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export function PayrollReportsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [scopes, setScopes] = useState<PrpScopes | null>(null);
  const [opts, setOpts] = useState<{ key: string; o: PrpOptions } | null>(null);
  const [err, setErr] = useState('');
  // A run a link named that the server does not know for this client (404 to its options): the pane falls back to the newest.
  const [missing, setMissing] = useState('');
  const scopeKind: 'run' | 'month' = params.get('rscope') === 'month' ? 'month' : 'run';
  useEffect(() => {
    let alive = true;
    payrollReportsApi.scopes(ctx.clientId, ctx.periodId, page)
      .then(d => { if (alive) { setScopes(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setScopes(null); setErr(prpErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const runs = scopes?.runs.rows || [];
  const months = scopes?.months || [];
  // The choice the URL names, else the newest. A run the picker's page does not list is still the one asked about (its own
  // options GET is the lookup: 200 for any run of this client), unless that GET said the server does not know it; a month
  // the period does not list is not (every month the period's posted runs own is on the picker). Nothing is chosen before
  // the scopes are read, so the newest is never asked about on the way to the run the link names.
  const wantRun = params.get('rrun') || '', wantYm = params.get('rym') || '';
  const runId = scopeKind !== 'run' || !scopes ? '' : wantRun && wantRun !== missing ? wantRun : runs[0]?.id || '';
  const ym = scopeKind === 'month' ? (months.some(m => ymOf(m.year, m.month) === wantYm) ? wantYm : months[0] ? ymOf(months[0].year, months[0].month) : '') : '';
  // The scope the pane settled on is not the one the address names (a month the period does not list, a run the server
  // does not know, or no choice named at all): its ?rkind must not open a window on it.
  const away = !!scopes && (scopeKind === 'run' ? !wantRun || runId !== wantRun : !wantYm || ym !== wantYm);
  /** The chosen run, when this page of the picker does not list it: drawn from its own options answer. */
  const offPage = scopeKind === 'run' && !!runId && !!scopes && !runs.some(r => r.id === runId);
  const scope: PrpScope | null = runId ? { kind: 'run', runId } : ym ? { kind: 'month', ym } : null;
  const scopeKey = runId ? `run:${runId}` : ym ? `month:${ym}` : '';
  useEffect(() => {
    if (!scopeKey) return;
    let alive = true;
    const s: PrpScope = scopeKey.startsWith('run:') ? { kind: 'run', runId: scopeKey.slice(4) } : { kind: 'month', ym: scopeKey.slice(6) };
    // A run off the picker's page that the server does not know: no error box, the newest instead (the notice says why).
    const lookup = offPage;
    payrollReportsApi.options(ctx.clientId, s)
      .then(o => { if (alive) { setOpts({ key: scopeKey, o }); setErr(''); } })
      .catch((e: any) => { if (!alive) return; if (lookup && s.kind === 'run' && Number(e?.status) === 404) setMissing(s.runId); else setErr(prpErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, scopeKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const o = opts && opts.key === scopeKey ? opts.o : null;
  // ?rkind=<kind> opens that report's window — only on the scope the address names, and once the options say it is there
  // to read. One it will not open (another scope, or a report the options refuse) is dropped from the address.
  const rkind = params.get('rkind') || '';
  const openOpt: PrpOption | null = (!away && o && o.reports.find(r => r.kind === rkind && r.ok && !!r.formats?.json?.ok)) || null;
  const write = (fn: (n: URLSearchParams) => void) => setParams(p => { const n = new URLSearchParams(p); fn(n); return n; }, { replace: true });
  const dropKind = !!rkind && (away || (!!o && !openOpt));
  useEffect(() => { if (dropKind) write(n => { n.delete('rkind'); }); }, [dropKind]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setKind = (k: 'run' | 'month') => write(n => { n.set('rscope', k); n.delete('rrun'); n.delete('rym'); n.delete('rkind'); });
  const pickRun = (id: string) => write(n => { n.set('rscope', 'run'); n.set('rrun', id); n.delete('rym'); n.delete('rkind'); });
  const pickMonth = (v: string) => write(n => { n.set('rscope', 'month'); n.set('rym', v); n.delete('rrun'); n.delete('rkind'); });
  // Opening writes the whole address (scope, choice and report), so the URL is a link a colleague can open.
  const open = (kind: PrpKind) => write(n => {
    n.set('rscope', scopeKind); n.set('rkind', kind);
    if (runId) { n.set('rrun', runId); n.delete('rym'); } else { n.set('rym', ym); n.delete('rrun'); }
  });
  const close = () => write(n => { n.delete('rkind'); });
  const scopeChip = (k: 'run' | 'month', label: string) => (
    <button data-rscope={k} aria-pressed={k === scopeKind} onClick={() => setKind(k)} style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(k === scopeKind ? chipOn : {}) }}>{label}</button>
  );
  const picker = scopeKind === 'run' ? (
    !runs.length && !offPage ? <div style={emptyLine} data-no-runs>{t('prp_no_runs')}</div> : (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-run-picker>
        {/* The run a link chose that this page does not list, first and pressed — read off its own options answer. */}
        {offPage && o?.scope.kind === 'run' && (
          <button key={`off:${runId}`} data-scope-run={runId} data-status={o.scope.status || ''} data-off-page aria-pressed onClick={() => pickRun(runId)}
            style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...chipOn }}>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{o.scope.docNo}</b> · {runTypeLabel(t, String(o.scope.runs[0]?.runType || ''))} · {runStatusLabel(t, String(o.scope.status || ''))}
          </button>
        )}
        {runs.map(r => (
          <button key={r.id} data-scope-run={r.id} data-status={r.status} aria-pressed={r.id === runId} onClick={() => pickRun(r.id)}
            style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(r.id === runId ? chipOn : {}) }}>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{r.docNo}</b> · {runTypeLabel(t, r.runType)} · {runStatusLabel(t, r.status)}
            <span style={{ color: 'var(--text-tertiary)' }}> · {dmy(r.periodFrom)} – {dmy(r.periodTo)}</span>
          </button>
        ))}
      </div>
    )
  ) : (
    !months.length ? <div style={emptyLine} data-no-months>{t('prp_no_months')}</div> : (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-month-picker>
        {months.map(m => {
          const v = ymOf(m.year, m.month);
          return (
            <button key={v} data-scope-month={v} aria-pressed={v === ym} onClick={() => pickMonth(v)} style={{ ...chip, flex: 'none', whiteSpace: 'nowrap', ...(v === ym ? chipOn : {}) }}>
              <b>{mmYyyy(m.year, m.month)}</b><span style={{ color: 'var(--text-tertiary)' }}> · {m.docNos.join(', ')}</span>
            </button>
          );
        })}
      </div>
    )
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-pane="payroll_reports" data-rscope-now={scopeKind}>
      {err && <div style={errBox} data-reports-error>{err}</div>}
      {scopeKind === 'run' && !!missing && missing === wantRun && <div style={errBox} data-linked-missing>{t('prp_err_not_found')}</div>}
      <div style={{ display: 'flex', gap: 6 }} data-rscope-switch>{scopeChip('run', t('prp_scope_run'))}{scopeChip('month', t('prp_scope_month'))}</div>
      <div style={card}>
        <div style={sectionHead}>{t(scopeKind === 'run' ? 'prp_pick_run' : 'prp_pick_month')}</div>
        {scopes === null ? (err ? null : <Loader />) : picker}
      </div>
      {scopeKind === 'run' && scopes && <Pagination page={page} total={scopes.runs.total} limit={scopes.runs.limit || 100} onPage={setPage} />}
      {scope && !o && !err && <Loader />}
      {o && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }} data-report-cards={scopeKey}>
          {PRP_REPORT_KINDS.map(kind => {
            const r = o.reports.find(x => x.kind === kind);
            if (!r) return null;
            const ready = r.ok && !!r.formats?.json?.ok;
            const why = r.ok ? (r.formats?.json?.refusals || [])[0] : r.refusals[0];
            return (
              <div key={kind} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }} data-report-card={kind} data-ok={ready ? '1' : '0'}>
                <div style={{ fontSize: 13.5, fontWeight: 800 }}>{kindTitle(t, kind)}</div>
                <div style={{ ...note, flex: 1 }}>{t(`prp_kind_${kind}_desc`)}</div>
                {ready ? (
                  <div><button style={actBtn} onClick={() => open(kind)} data-open-report={kind}><i className="ti ti-report-analytics" aria-hidden /> {t('prp_open')}</button></div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }} data-card-refusal={why?.code || ''}>
                    <i className="ti ti-info-circle" aria-hidden /> {refusalText(t, kind, why, o.scope)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {o && openOpt && scope && <ReportWindow key={`${scopeKey}:${openOpt.kind}`} ctx={ctx} opt={openOpt} scope={scope} scopeOut={o.scope} onClose={close} />}
    </div>
  );
}

// ── One report, full screen, read only ───────────────────────────────────────────────────────────────────────────────
// The JSON the window shows (100 rows a page), its banners and Filing Center notes, the table from the answer's own
// columns, the totals over every row, and the checks (✓ / ✗, with the figures when one fails). Top right: Excel and PDF,
// each only when its format is offered, saved under the server's own file name (§4.8). Nothing in it can be typed into,
// so it has no `dirty`: every exit just closes (and drops ?rkind).
function ReportWindow({ ctx, opt, scope, scopeOut, onClose }: { ctx: AwCtx; opt: PrpOption; scope: PrpScope; scopeOut: PrpScopeOut; onClose: () => void }) {
  const { t } = useTranslation();
  const kind = opt.kind;
  const [page, setPage] = useState(1);
  const [rep, setRep] = useState<PrpReport | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollReportsApi.report(ctx.clientId, kind, scope, page)
      .then(r => { if (alive) { setRep(r); setErr(''); } })
      .catch((e: any) => { if (alive) setErr(prpErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, kind, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dl = (fmt: 'xlsx' | 'pdf') => {
    const f = opt.formats?.[fmt];
    if (!f?.ok) return <span key={fmt} style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }} data-dl-refusal={fmt}>{refusalText(t, kind, (f?.refusals || [])[0], scopeOut)}</span>;
    return (
      <span key={fmt} data-dl={fmt} style={{ flex: 'none' }}>
        <DlBtn style={actBtn} onRun={() => payrollReportsApi.download(ctx.clientId, kind, scope, fmt, reportFileName(kind, scopeOut, fmt))}>
          <i className={fmt === 'xlsx' ? 'ti ti-file-spreadsheet' : 'ti ti-file-type-pdf'} aria-hidden /> {t(fmt === 'xlsx' ? 'prp_excel' : 'prp_pdf')}
        </DlBtn>
      </span>
    );
  };
  const runsText = scopeOut.kind === 'month' && scopeOut.runs.length ? ` · ${scopeOut.runs.map(r => r.docNo).join(', ')}` : '';
  return (
    <Dialog fullscreen footer={<span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }} data-report-foot>{rep ? t('prp_n_rows', { count: rep.rows.total }) : ''}</span>}
      title={kindTitle(t, kind)} docNo={scopeText(scopeOut)} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-report-window={kind} data-scope={scopeOut.tag}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }} data-report-scope>
            {t('prp_scope_tag', { scope: t(scopeOut.kind === 'run' ? 'prp_scope_run' : 'prp_scope_month'), tag: scopeText(scopeOut) })}{runsText}
          </div>
          {dl('xlsx')}{dl('pdf')}
        </div>
        {err && <div style={errBox} data-report-error>{err}</div>}
        {!rep ? (err ? null : <Loader />) : <ReportBody rep={rep} t={t} page={page} onPage={setPage} />}
      </div>
    </Dialog>
  );
}

function ReportBody({ rep, t, page, onPage }: { rep: PrpReport; t: TF; page: number; onPage: (p: number) => void }) {
  const kind = rep.kind;
  const cols = rep.columns || [];
  const rows = rep.rows?.rows || [];
  const totals = rep.totals || {};
  const hasTotals = cols.some(c => totals[c.key] !== undefined && totals[c.key] !== null);
  const head = (c: PrpColumn): string => {
    if (c.key.startsWith('run:')) return String(rep.scope.runs.find(r => r.runId === c.key.slice(4))?.docNo || c.en || c.key);
    if (kind === 'details' && PRP_COL_BEFORE.includes(c.key)) return t(`prp_col_before_${c.key}`);
    const k = `prp_col_${c.key}`;
    return t(k) !== k ? t(k) : c.en || c.key;
  };
  const right = (c: PrpColumn) => c.type === 'money' || c.type === 'count';
  return (
    <>
      {(rep.banners || []).map(b => (
        <div key={b} style={{ ...card, ...amberBox, fontSize: 12.5, display: 'flex', gap: 8 }} data-banner={b}>
          <i className="ti ti-alert-triangle" aria-hidden style={{ marginTop: 2 }} /><div>{t(`prp_banner_${b}`)}</div>
        </div>
      ))}
      {(rep.advisories || []).map((a, i) => (
        <div key={`${a.code}:${i}`} style={{ ...card, ...amberBox, fontSize: 12.5 }} data-advisory={a.code}>{advisoryText(t, a)}</div>
      ))}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!rows.length ? <div style={emptyLine} data-report-empty>{t('prp_n_rows', { count: 0 })}</div> : (
          <table style={table} data-report-table={kind}>
            <thead>
              <tr>{cols.map(c => <th key={c.key} style={{ ...th, ...(right(c) ? { textAlign: 'right' } : {}), whiteSpace: 'nowrap' }} data-col={c.key}>{head(c)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={String(r.lineId || r.id || i)} data-row={i}>
                  {cols.map(c => <td key={c.key} style={right(c) ? num : td} data-cell={c.key} data-type={c.type} data-v={cellAttr(r[c.key])}>{cellText(t, kind, c, r[c.key], r)}</td>)}
                </tr>
              ))}
            </tbody>
            {hasTotals && (
              <tfoot>
                <tr data-totals>
                  {cols.map((c, i) => {
                    const v = totals[c.key];
                    const has = v !== undefined && v !== null;
                    return <td key={c.key} style={{ ...(right(c) ? num : td), fontWeight: 800 }} data-total={c.key} data-v={has ? String(v) : undefined}>
                      {has ? (c.type === 'money' ? m2(v) : String(v)) : i === 0 ? t('prp_total') : ''}
                    </td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
      <Pagination page={page} total={rep.rows?.total || 0} limit={rep.rows?.limit || 100} onPage={onPage} />
      {(rep.checks || []).length > 0 && (
        <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: 4 }} data-checks>
          <div style={sectionHead}>{t('prp_checks')}</div>
          {rep.checks.map((c, i) => <CheckLine key={`${c.code}:${c.runId || ''}:${c.accountId || ''}:${i}`} c={c} rep={rep} t={t} />)}
        </section>
      )}
    </>
  );
}
function CheckLine({ c, rep, t }: { c: PrpCheck; rep: PrpReport; t: TF }) {
  const run = c.runId ? rep.scope.runs.find(r => r.runId === c.runId)?.docNo : '';
  const acct = c.accountId ? rep.rows.rows.find(r => r.accountId === c.accountId)?.accountCode : '';
  const k = `prp_check_${c.code}`;
  return (
    <div style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'baseline' }} data-check={c.code} data-check-ok={c.ok ? '1' : '0'} data-check-run={c.runId || undefined}
      data-check-account={c.accountId || undefined}>
      <i className={c.ok ? 'ti ti-circle-check' : 'ti ti-circle-x'} aria-hidden style={c.ok ? okBox : badBox} />
      <div>
        <span>{t(k) !== k ? t(k) : c.code}</span>{run ? <b> · {run}</b> : null}{acct ? <b> · {acct}</b> : null}
        {c.ok && c.actualSat !== null && c.actualSat !== undefined ? <span style={{ color: 'var(--text-tertiary)' }}> · {m2(c.actualSat)}</span> : null}
        {!c.ok && c.expectedSat !== null && c.expectedSat !== undefined ? (
          <span style={badBox}> — {t('prp_check_figures', { expected: m2(c.expectedSat), actual: m2(c.actualSat) })}</span>
        ) : null}
      </div>
    </div>
  );
}
function advisoryText(t: TF, a: PrpAdvisory): string {
  const k = `prp_adv_${a.code}`;
  const opts = { form: FORM_OF[a.kind as PrpKind] || PND1, filing: m2(a.filingSat), total: m2(a.totalSat), n: Number(a.count) || 0, diff: m2(Math.abs(Number(a.diffSat) || 0)) };
  return t(k) !== k ? t(k, opts) : String(a.code || '');
}
/** A code in its family's words (PRP_CODE_FAMILIES); a list of codes, each worded. */
function codeWord(t: TF, kind: PrpKind, col: string, v: any, row: Record<string, any>): string {
  const fam = PRP_CODE_FAMILIES[kind]?.[col];
  const one = (x: any): string => {
    const s = String(x ?? '');
    if (!fam || !s) return s;
    const k = `${fam.replace('<block>', String(row.block || ''))}${s}`;
    return t(k) !== k ? t(k, { form: PND1 }) : s;
  };
  return Array.isArray(v) ? v.map(one).filter(Boolean).join(', ') : one(v);
}
/** A cell by its column's type (§4.4): money from satang, dates day-first, a flag as ✓, lists comma-joined. */
function cellText(t: TF, kind: PrpKind, c: PrpColumn, v: any, row: Record<string, any>): string {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return c.type === 'flag' ? '' : '—';
  switch (c.type) {
    case 'money': return m2(v);
    case 'count': return String(v);
    case 'date': return dmy(String(v));
    case 'dates': return (Array.isArray(v) ? v : [v]).map(x => dmy(String(x))).join(', ');
    case 'code': return codeWord(t, kind, c.key, v, row);
    case 'flag': return v ? '✓' : '';
    default: return Array.isArray(v) ? v.map(String).join(', ') : String(v);
  }
}
const cellAttr = (v: any): string | undefined => (v === null || v === undefined ? undefined : Array.isArray(v) ? v.join(',') : String(v));
